import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { GetAccounts, GetAccountQueue, GetAccountsName } from '../../services/backendServices';
import { ACCOUNT_QUEUE_STATUS } from '../../utils/key_helper';
import toast from 'react-hot-toast';
// Create the AccountContext to share data across components
const AccountContext = createContext();

// AccountContextProvider: A provider component that manages and supplies account-related data to its children.

export const AccountContextProvider = ({ children }) => {
    const [accountData, setAccountData] = useState([]);// State to store the list of accounts
    const [isAccountLoading, setIsAccountLoading] = useState(false);   // State to track if account data is being loaded
    const [isAccountDataFetched, setIsAccountDataFetched] = useState(false);  // State to check if account data has already been fetched
    const [myAccountData, setMyAccountData] = useState();// State to store account names for dropdown selections
    const [isAccountQueueFetched, setIsAccountQueueFetched] = useState(false);
    const [accountQueueData, setAccountQueueData] = useState([]);
    const [accountOptions, setAccountOptions] = useState([]);

    // Function the Fetches all accounts 
    const fetchAccountData = async () => {
        setIsAccountLoading(true);
        try {
            const res = await GetAccounts();
            if (res.status) {
                setAccountData(res.data);
                setIsAccountDataFetched(true);
            }
        } catch (e) {
            console.error("Error fetching Account:", e);
        } finally {
            setIsAccountLoading(false);
        }
    };
    // Function the fetches account queue data
    const fetchAccountQueueData = async () => {
        try {
            const res = await GetAccountQueue();
            if (res.status) {
                setAccountQueueData(res.data);
                setIsAccountQueueFetched(true);
            }
        } catch (e) {
            console.error("Error fetching Account Queue Data:", e);
        }
    };
    useEffect(() => {
        if (!isAccountQueueFetched) {
            fetchAccountQueueData();
        }
    }, [isAccountQueueFetched])
    // Function to refresh account data manually
    const removeQueueData = (queueId) => {
        setAccountQueueData((prev) => prev.filter((item) => item.id !== queueId));
    };

    // Function to update the status of a queue item
    const updateQueueStatus = useCallback((queueId, newStatus, newRemark) => {
        setAccountQueueData((prev) =>
            prev.map((item) =>
                item.id === queueId ? { ...item, status: newStatus, remark: newRemark } : item
            )
        );
    }, []);



    // Function to add a new account
    const addAccountData = (newData) => {
        setAccountData(prevData => [...prevData, newData]);
    };

    // Function to update an existing account by broker_id
    const updateAccountData = (updatedData) => {
        setAccountData((prevData) =>
            prevData.map((item) =>
                item.broker_id === updatedData.broker_id ? { ...item, ...updatedData } : item
            )
        );
    };
    // Function to delete Account Data 
    const deleteAccount = (broker_id) => {
        setAccountData((prevData) => prevData.filter((item) => item.broker_id !== broker_id));
    };

    // Function to check if a broker is renewing an account
    const isBrokerRenewing = useCallback(
        (brokerId) =>
            accountQueueData.some(
                (item) =>
                    item.broker_id === brokerId &&
                    item.status === ACCOUNT_QUEUE_STATUS.RENEWING
            ),
        [accountQueueData]
    );
    //Function to update account info data 
    const updateAccountInfo = (broker_id, updatedAccountInfo) => {
        setAccountData((prevData) =>
            prevData.map((item) =>
                item.broker_id === broker_id
                    ? {
                        ...item,
                        account_info: {
                            ...item.account_info,
                            ...updatedAccountInfo
                        }
                    }
                    : item
            )
        );
    };
    //Refreshes account data and shows a toast notification
    const handleRefresAccountData = async () => {
        try {
            await fetchAccountData();
            toast.success("Data refreshed successfully!");
        } catch (error) {
            toast.error("Error refreshing data. Please try again.");
        }
    };
    // Function the Fetches all accounts  namesto be used for dropdown selections
    const fetchAccountNames = async () => {
        try {
            const res = await GetAccountsName();
            if (res.status) {
                const accountOptions = res.data.map((account) => ({
                    value: account.broker_id,
                    label: account.broker_combine_name,
                }));
                setAccountOptions(accountOptions);
            }
        } catch (error) {
            console.error("Error fetching account names:", error);
        }
    };

    return (
        // Provide account data and functions to child components
        <AccountContext.Provider
            value={{
                accountData, // List of connected account data
                isAccountLoading, // Loading status
                isAccountDataFetched, // Data fetched status
                setIsAccountDataFetched, // Function to manually set fetched status
                addAccountData, // Function to add a new account
                updateAccountData, // Function to update an existing account
                fetchAccountData, // Function to fetch account data
                fetchAccountQueueData,
                isAccountQueueFetched,
                accountQueueData,
                setIsAccountQueueFetched,
                setAccountQueueData, handleRefresAccountData,
                removeQueueData, updateQueueStatus,
                isBrokerRenewing, deleteAccount, updateAccountInfo,
                fetchAccountNames, accountOptions,
            }}
        >
            {children}
        </AccountContext.Provider>
    );
};

// Custom hook to easily access AccountContext in other components
export const useAccountContext = () => {
    return useContext(AccountContext);
};
